import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/lib/api';
import Sidebar from '@/components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { FileSignature, Plus, Trash2, Send, ArrowRight, Copy } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit } from '@/lib/permissions';

const STATUS_COLORS = {
  draft: 'bg-slate-200 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-rose-100 text-rose-700',
  expired: 'bg-amber-100 text-amber-700',
  converted: 'bg-purple-100 text-purple-700',
};

const fmtMoney = (n, currency = 'AUD') => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(n || 0);

export default function Quotes() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientFilter = searchParams.get('client_id');
  const eventFilter = searchParams.get('event_id');

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);

  const canManage = canEdit(user, 'quotes');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, cRes, eRes] = await Promise.all([
        api.get('/quotes', { params: { client_id: clientFilter || undefined, event_id: eventFilter || undefined } }),
        api.get('/clients'),
        api.get('/events'),
      ]);
      setQuotes(qRes.data || []);
      setClients(cRes.data || []);
      setEvents(eRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load quotes');
    } finally {
      setLoading(false);
    }
  }, [clientFilter, eventFilter]);

  useEffect(() => { load(); }, [load]);

  if (quoteId) {
    return <QuoteEditor quoteId={quoteId} clients={clients} events={events} canManage={canManage} onSaved={load} onClose={() => navigate('/quotes')} />;
  }

  const clientName = (id) => clients.find((c) => c.id === id)?.name || '—';

  const createNew = async () => {
    if (clients.length === 0) {
      toast.error('Create a client first');
      navigate('/clients');
      return;
    }
    try {
      const r = await api.post('/quotes', { client_id: clientFilter || clients[0].id, items: [] });
      navigate(`/quotes/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create quote');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <FileSignature className="w-7 h-7 text-emerald-600" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Quotes</h1>
                <p className="text-sm text-slate-500">Estimates sent to clients before work starts. Convert accepted quotes into invoices.</p>
              </div>
            </div>
            {canManage && <Button onClick={createNew}><Plus className="w-4 h-4 mr-1" /> New quote</Button>}
          </div>

          {loading ? (
            <p className="text-center text-slate-500 py-8">Loading…</p>
          ) : quotes.length === 0 ? (
            <Card className="glass-card p-12 text-center text-slate-500">
              <FileSignature className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No quotes yet.</p>
            </Card>
          ) : (
            <Card className="glass-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-xs text-slate-600">
                    <th className="px-4 py-2">Number</th>
                    <th className="px-4 py-2">Client</th>
                    <th className="px-4 py-2">Title</th>
                    <th className="px-4 py-2">Total</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Created</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono text-xs">{q.number}</td>
                      <td className="px-4 py-2">{clientName(q.client_id)}</td>
                      <td className="px-4 py-2 text-slate-700">{q.title || '—'}</td>
                      <td className="px-4 py-2 font-semibold">{fmtMoney(q.total, q.currency)}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[q.status] || 'bg-slate-100'}`}>{q.status}</span>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">{q.created_at?.slice(0, 10) || ''}</td>
                      <td className="px-4 py-2 text-right">
                        <Link to={`/quotes/${q.id}`} className="text-emerald-600 hover:text-emerald-700 text-xs">Open →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}

function QuoteEditor({ quoteId, clients, events, canManage, onSaved, onClose }) {
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photographers, setPhotographers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, pRes] = await Promise.all([
        api.get(`/quotes/${quoteId}`),
        api.get('/photographers'),
      ]);
      setQuote(qRes.data);
      setPhotographers(pRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load quote');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [quoteId, onClose]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => setQuote((q) => ({ ...q, ...patch }));
  const updateItem = (idx, patch) => setQuote((q) => ({ ...q, items: q.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addItem = () => setQuote((q) => ({ ...q, items: [...(q.items || []), { id: crypto.randomUUID(), description: '', quantity: 1, unit: 'flat', unit_price: 0 }] }));
  const removeItem = (idx) => setQuote((q) => ({ ...q, items: q.items.filter((_, i) => i !== idx) }));

  const addPhotographerLine = (photographerId) => {
    const p = photographers.find((x) => x.id === photographerId);
    if (!p) return;
    const rate = p.bill_rate ?? p.hourly_rate ?? 0;
    setQuote((q) => ({ ...q, items: [...(q.items || []), {
      id: crypto.randomUUID(),
      description: `${p.name} — photographer hours`,
      quantity: 1,
      unit: 'hour',
      unit_price: rate,
      photographer_id: p.id,
    }] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/quotes/${quoteId}`, {
        title: quote.title, intro_html: quote.intro_html,
        items: quote.items, currency: quote.currency,
        tax_rate: quote.tax_rate, discount: quote.discount,
        valid_until: quote.valid_until, notes: quote.notes,
        status: quote.status,
      });
      setQuote(r.data);
      toast.success('Quote saved');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const sendQuote = async () => {
    if (!window.confirm('Mark as sent and generate the share link?')) return;
    try {
      const r = await api.post(`/quotes/${quoteId}/send`);
      setQuote(r.data);
      const link = `${window.location.origin}${r.data.share_url}`;
      navigator.clipboard.writeText(link).catch(() => {});
      toast.success('Quote marked as sent · link copied');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send');
    }
  };

  const convert = async () => {
    if (!window.confirm('Convert this quote into an invoice?')) return;
    try {
      const r = await api.post(`/quotes/${quoteId}/convert-to-invoice`);
      toast.success('Invoice created');
      navigate(`/invoices/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not convert');
    }
  };

  const deleteQuote = async () => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await api.delete(`/quotes/${quoteId}`);
      toast.success('Deleted');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  const copyShareLink = () => {
    if (!quote.public_token) return;
    const link = `${window.location.origin}/share/quote/${quote.public_token}`;
    navigator.clipboard.writeText(link).catch(() => {});
    toast.success('Share link copied');
  };

  if (loading || !quote) {
    return <div className="min-h-screen bg-slate-50"><Sidebar /><main className="md:ml-64 p-8"><p>Loading…</p></main></div>;
  }

  const totals = computeTotals(quote);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <Button variant="ghost" size="sm" onClick={onClose}>← Back</Button>
              <h1 className="text-2xl font-bold text-slate-900 mt-2">
                Quote <span className="font-mono text-emerald-600">{quote.number}</span>
                <span className={`ml-3 text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[quote.status] || 'bg-slate-100'}`}>{quote.status}</span>
              </h1>
            </div>
            {canManage && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                {quote.status === 'draft' && <Button size="sm" onClick={sendQuote}><Send className="w-3.5 h-3.5 mr-1" /> Mark sent</Button>}
                {(quote.status === 'sent' || quote.status === 'accepted') && quote.public_token && <Button variant="outline" size="sm" onClick={copyShareLink}><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</Button>}
                {quote.status === 'accepted' && <Button size="sm" onClick={convert}><ArrowRight className="w-3.5 h-3.5 mr-1" /> Convert to invoice</Button>}
                <Button variant="ghost" size="sm" onClick={deleteQuote} className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>

          <Card className="glass-card p-5 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Client</Label>
                <Select value={quote.client_id} onValueChange={(v) => update({ client_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Event (optional)</Label>
                <Select value={quote.event_id || ''} onValueChange={(v) => update({ event_id: v || null })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">— None —</SelectItem>
                    {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valid until</Label>
                <Input type="date" value={quote.valid_until || ''} onChange={(e) => update({ valid_until: e.target.value })} />
              </div>
              <div className="md:col-span-3">
                <Label>Title</Label>
                <Input value={quote.title || ''} onChange={(e) => update({ title: e.target.value })} placeholder="Coverage of Sydney Marathon 2026" />
              </div>
              <div className="md:col-span-3">
                <Label>Intro</Label>
                <textarea rows={3} value={quote.intro_html || ''} onChange={(e) => update({ intro_html: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" placeholder="Plain text or simple HTML" />
              </div>
            </div>
          </Card>

          <Card className="glass-card p-5 mb-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-semibold text-slate-900">Line items</h2>
              <div className="flex gap-2 flex-wrap">
                {photographers.length > 0 && (
                  <Select value="" onValueChange={addPhotographerLine}>
                    <SelectTrigger className="h-8 w-56"><SelectValue placeholder="+ Photographer line" /></SelectTrigger>
                    <SelectContent>
                      {photographers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({fmtMoney(p.bill_rate ?? p.hourly_rate ?? 0, quote.currency)}/hr)</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button size="sm" variant="outline" onClick={addItem}><Plus className="w-3.5 h-3.5 mr-1" /> Add line</Button>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500 border-b">
                <tr>
                  <th className="py-1.5 pr-2">Description</th>
                  <th className="py-1.5 pr-2 w-24">Qty</th>
                  <th className="py-1.5 pr-2 w-28">Unit</th>
                  <th className="py-1.5 pr-2 w-32">Unit price</th>
                  <th className="py-1.5 pr-2 w-28 text-right">Total</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(quote.items || []).map((it, i) => (
                  <tr key={it.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2">
                      <Input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} className="h-8" />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input type="number" step="0.5" value={it.quantity} onChange={(e) => updateItem(i, { quantity: parseFloat(e.target.value) || 0 })} className="h-8" />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Select value={it.unit || 'flat'} onValueChange={(v) => updateItem(i, { unit: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flat">flat</SelectItem>
                          <SelectItem value="hour">hour</SelectItem>
                          <SelectItem value="day">day</SelectItem>
                          <SelectItem value="photographer-hour">photographer-hour</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: parseFloat(e.target.value) || 0 })} className="h-8" />
                    </td>
                    <td className="py-1.5 pr-2 text-right font-medium">
                      {fmtMoney((it.quantity || 0) * (it.unit_price || 0), quote.currency)}
                    </td>
                    <td className="py-1.5">
                      <button onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {(quote.items || []).length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-400 py-3 text-xs">No line items yet</td></tr>
                )}
              </tbody>
            </table>

            <div className="mt-4 flex flex-col items-end gap-1 text-sm">
              <div className="flex gap-3 items-center"><span className="text-slate-500">Subtotal:</span><span className="font-medium w-28 text-right">{fmtMoney(totals.subtotal, quote.currency)}</span></div>
              <div className="flex gap-3 items-center"><span className="text-slate-500">Discount:</span>
                <Input type="number" step="0.01" value={quote.discount} onChange={(e) => update({ discount: parseFloat(e.target.value) || 0 })} className="h-7 w-24 text-right" />
              </div>
              <div className="flex gap-3 items-center"><span className="text-slate-500">Tax rate:</span>
                <Input type="number" step="0.01" value={quote.tax_rate} onChange={(e) => update({ tax_rate: parseFloat(e.target.value) || 0 })} className="h-7 w-24 text-right" />
              </div>
              <div className="flex gap-3 items-center"><span className="text-slate-500">Tax:</span><span className="font-medium w-28 text-right">{fmtMoney(totals.tax, quote.currency)}</span></div>
              <div className="flex gap-3 items-center text-base"><span className="font-semibold">Total:</span><span className="font-bold w-28 text-right">{fmtMoney(totals.total, quote.currency)}</span></div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}

function computeTotals(doc) {
  const items = doc.items || [];
  const subtotal = items.reduce((s, it) => s + (it.quantity || 0) * (it.unit_price || 0), 0);
  const discount = doc.discount || 0;
  const taxable = Math.max(0, subtotal - discount);
  const tax = taxable * (doc.tax_rate || 0);
  return { subtotal, tax, total: taxable + tax };
}
